import { apiClient } from '@/lib/api/client'
import { DoctorOverview } from '@/types'
import { Appointment } from '@/types/appointment'

export interface DashboardStats {
  total_users: number
  total_media: number
  total_categories: number
  total_tags: number
}

export interface EnhancedDashboardStats extends DashboardStats {
  active_users: number
  new_users_today: number
  new_users_this_week: number
  new_users_this_month: number
  users_last_30_days: number
  total_pages: number
  published_pages: number
  draft_pages: number
  pages_this_month: number
  media_this_month: number
  activities_today: number
  activities_this_week: number
}

export interface DashboardWidget {
  id: number
  user_id: number | null
  widget_type: 'metric' | 'line_chart' | 'bar_chart' | 'pie_chart' | 'table'
  title: string
  data_source: string
  query_config: Record<string, any>
  visualization_config: Record<string, any>
  position_x: number
  position_y: number
  width: number
  height: number
  order_index: number
  active: boolean
  created_at: string
  updated_at: string
}

export interface ChartData {
  labels: string[]
  data: number[]
}
export interface Doctor7DaysSummary {
  total_patients: { daily: number[]; change: number }
  total_appointments: { daily: number[]; change: number }
  pending_appointments: { daily: number[]; change: number }
  completed_appointments: { daily: number[]; change: number }
}
export interface DoctorDashboard {
  summary: {
    total_patients: number
    total_appointments: number
    pending_appointments: number
    completed_appointments: number
    last_7_days: Doctor7DaysSummary
  }
  statistics: {
    total_appointments: number
    cancelled: number
    completed: number
    rescheduled: number
    per_month: {
      month: string
      total: number
      rescheduled: number
      cancelled: number
      completed: number
    }[]
    appointments_yesterday: number
    appointments_today: number
  }
  upcoming_appointments: Appointment[]
  all_appointments: Appointment[]
}

export interface AdminDashboard {
  summary: {
    total_doctors: number
    total_patients: number
    total_appointments: number
    total_revenue: number
    last_7_days: Admin7DaysSummary
  }
  statistics: {
    total_appointments: number
    cancelled: number
    completed: number
    rescheduled: number
    per_month: {
      month: string
      total: number
      completed: number
      cancelled: number
      ongoing: number
      rescheduled: number
    }[]
  }
  appointments: Appointment[]
  popular_doctors: PopularDoctor[]
  available_doctors: AvailableDoctor[]
  top_patients: TopPatient[]
  top_services: TopServices[]
  all_appointments: Appointment[]
}

export interface Admin7DaysSummary {
  total_doctors: {
    daily: number[]
    change: number
  }
  total_patients: {
    daily: number[]
    change: number
  }
  total_appointments: {
    daily: number[]
    change: number
  }
  total_revenue: {
    daily: number[]
    change: number
  }
}
export interface AvailableDoctor {
  id: string
  name: string
  department: string
  avatar?: string | null
}

export interface PopularDoctor {
  id: string
  name: string
  department: string
  bookings: number
  avatar?: string | null
}
export interface TopPatient {
  id: string
  name: string
  email: string
  phone: string
  total_paid: string
  appointments: number
  avatar?: string | null
}
export interface TopServices {
  id: string
  name: string
  times_used: number
  total_revenue: string
}

export const dashboardService = {
  /**
   * Get basic dashboard statistics
   */
  getStats: async (): Promise<DashboardStats> => {
    const response = await apiClient.get('/dashboard/stats')
    return response.data.data
  },

  /**
   * Get enhanced dashboard statistics
   */
  getEnhancedStats: async (): Promise<EnhancedDashboardStats> => {
    const response = await apiClient.get('/dashboard/enhanced-stats')
    return response.data.data
  },

  /**
   * Get all dashboard widgets
   */
  getWidgets: async (): Promise<DashboardWidget[]> => {
    const response = await apiClient.get('/dashboard/widgets')
    return response.data.data
  },

  /**
   * Get widget data
   */
  getWidgetData: async (widgetId: number): Promise<any> => {
    const response = await apiClient.get(`/dashboard/widgets/${widgetId}/data`)
    return response.data.data
  },

  /**
   * Get user registration trend
   */
  getUserRegistrationTrend: async (
    period: 'daily' | 'weekly' | 'monthly' = 'daily',
    days: number = 30
  ): Promise<ChartData> => {
    const response = await apiClient.get('/dashboard/user-registration-trend', {
      params: { period, days },
    })
    return response.data.data
  },

  /**
   * Get pages by status
   */
  getPagesByStatus: async (): Promise<ChartData> => {
    const response = await apiClient.get('/dashboard/pages-by-status')
    return response.data.data
  },

   /**
   * Get doctor dashboard
   */
  getDoctorDashboard: async (): Promise<DoctorDashboard> => {
    const response = await apiClient.get('/dashboard/doctor-dashboard')
    return response.data.data
  }
,
   /**
   * Get web admin dashboard
  */
  getWebAdminDashboard: async (): Promise<AdminDashboard> => {
    const response = await apiClient.get('/dashboard/web-admin-dashboard')
    return response.data.data
  }
}

